import * as React from 'react';
import {
  Table,
  TableHead,
  TableCell,
  Typography,
  TableRow,
  TableBody,
} from '@mui/material';
import { Cell, CellProps, RowProps } from '../TableShared/Cell';
import { colors } from '../colors';
import { neutral } from '../../themes/colors';

const toCSSWidth = (value: ColumnSchema['width']) => ({
  width: {
    sm: value === 'stretchable' ? 'auto' : '60px',
    md: value === 'stretchable' ? 'auto' : value + 'px',
  },
  minWidth: {
    sm: value === 'stretchable' ? '120px' : '60px',
    md: value === 'stretchable' ? '240px' : '60px',
  },
});

export type ColumnSchema = {
  label?: string;
  width: 'stretchable' | number;
  maxWidth?: string | number;
  colSpan?: number;
  cellWidth?: 'stretchable' | number;
  justify?: 'center' | 'right';
  onClick?: (value: any) => void;
  sort?: {
    enabled: boolean;
    isAsc: boolean;
    onClick: () => void;
  };
};

export type Row<T extends string | number = string> = {
  cells: Array<CellProps>;
  single?: RowProps;
  id: T;
};
export type StandardTableProps<T extends string | number = string> = {
  schema: Array<ColumnSchema>;
  noHeader?: true;
  rows: Array<Row<T>>;
  sx?: React.CSSProperties;
  className?: string;
};

export const StandardTable = <T extends string | number = string>(
  p: StandardTableProps<T>,
) => {
  const renderRow = (row: Row<T>, index: number) => {
    const style = {
      display: 'inline-table',
      width: '100%',
      marginBottom: '0.5em',
      marginTop: index !== 0 ? '0.5em' : undefined,
    };

    if (row.single)
      return (
        <TableRow key={index} role="row" sx={style}>
          <Cell {...row.single} />
        </TableRow>
      );

    return (
      <TableRow
        key={row.id || 'row' + index}
        role="row"
        sx={{
          ...style,
          backgroundColor: neutral['50'],
          borderRadius: '10px',
          boxShadow: `0px 0px 1px ${neutral['200']}, 0px 1px 2px rgba(29, 41, 57, 0.08)`,
        }}
      >
        {row.cells.map((cell, i) => {
          const schemaWidth =
            i < p.schema.length
              ? (p.schema[i].cellWidth ?? p.schema[i].width)
              : 'stretchable';

          return (
            <TableCell
              key={row.id + cell.type + i}
              sx={{
                ...toCSSWidth(schemaWidth),
                textAlign: p.schema[i]?.justify || 'left',
              }}
            >
              <Cell {...cell} index={index} />
            </TableCell>
          );
        })}
      </TableRow>
    );
  };

  return (
    <Table sx={{ userSelect: 'none', ...(p.sx || {}) }} className={p.className}>
      {p.noHeader ? null : (
        <TableHead sx={{ backgroundColor: 'transparent' }}>
          <TableRow sx={{ display: 'inline-table', width: '100%' }}>
            {p.schema.map((column, i) => (
              <TableCell
                key={(column.label || 'column') + i}
                sx={{
                  ...toCSSWidth(column.width),
                  textAlign: column.justify || 'left',
                }}
                colSpan={column.colSpan}
              >
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: 'uppercase',
                    color: colors.text800,
                  }}
                >
                  {column.label}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
      )}
      <TableBody>{p.rows.map(renderRow)}</TableBody>
    </Table>
  );
};
